import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';

import { useCommon } from 'src/AppState/Common';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from 'src/domains/common/components/dialog/dialog';
import { OptedInAccountsDialogState } from './OptedInAccountsDialog.state';
import { Button } from 'src/domains/common/components/button/button';
import { Input } from 'src/domains/common/components/input/input';
import { ArrowUpDownIcon } from 'lucide-react';
import { cn } from 'src/domains/common/components/utils';
import { PaginationTable } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { useAppStateContext } from 'src/AppState/AppState';
import { DateTime } from 'src_common/utils/time/time';

export interface OptedInAccountsDialogPropsType {
    campaignId: string;
}

export const OptedInAccountsDialog = observer(
    'OptedInAccountsDialog',
    ({ campaignId }: OptedInAccountsDialogPropsType) => {
        const common = useCommon();
        const { routerBackoffice } = useAppStateContext();

        const [optedInAccountsDialogState] = useState(
            () => new OptedInAccountsDialogState(common, routerBackoffice, campaignId)
        );

        const {
            showOptInDialog,
            isOptInDialogShown,
            onGenerateCSV,
            filteredData,
            paginatedData,
            setSortConfig,
            search,
            setSearch,
            paginationConfig,
            isLoading,
            redirectToAccount,
        } = optedInAccountsDialogState;

        return (
            <>
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-4 underline pl-0'
                    onClick={() => {
                        showOptInDialog(true);
                    }}
                >
                    Yes
                </Button>
                {isOptInDialogShown && (
                    <Dialog
                        className='space-y-6'
                        onClickOutside={() => showOptInDialog(false)}
                    >
                        <DialogHeader>
                            <DialogTitle data-test='dialog-title'>
                                Opted-in Players{' '}
                                <span data-test='campaign-opt-in-count'>
                                    {isLoading === false &&
                                        `(${new Intl.NumberFormat('en-US').format(filteredData.length)})`}
                                </span>
                            </DialogTitle>
                        </DialogHeader>
                        <DialogFooter
                            data-test='dialog-footer'
                            className='w-full'
                        >
                            <div className='flex'>
                                <Input
                                    placeholder='Search'
                                    formInputState={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className='max-w-xs mb-4'
                                    errors={[]}
                                    dataTest='search-input'
                                />
                                <Button
                                    className='text-xs font-normal'
                                    onClick={onGenerateCSV}
                                    disabled={isLoading || filteredData.length === 0}
                                >
                                    Export CSV
                                </Button>
                            </div>

                            <div className='relative w-full overflow-auto max-h-96 border border-solid rounded-md border-border'>
                                <Table
                                    className={cn('table-fixed', {
                                        'opacity-50 animate-pulse': isLoading,
                                    })}
                                >
                                    <TableHeader className='sticky top-0 bg-white z-[1] shadow-b-shadow'>
                                        <TableRow className='!border-b-0'>
                                            <TableHead className='py-0 h-14'>
                                                <div
                                                    className='cursor-pointer flex items-center gap-2.5'
                                                    onClick={() => setSortConfig('accountId')}
                                                >
                                                    Account Id
                                                    <ArrowUpDownIcon className='size-4' />
                                                </div>
                                            </TableHead>

                                            <TableHead className='py-0 h-14'>
                                                <div
                                                    className='cursor-pointer flex items-center gap-2.5'
                                                    onClick={() => setSortConfig('createdAt')}
                                                >
                                                    Opted-in Timestamp (UK time)
                                                    <ArrowUpDownIcon className='size-4' />
                                                </div>
                                            </TableHead>

                                            <TableHead className='h-14 w-14'>&nbsp;</TableHead>
                                        </TableRow>
                                    </TableHeader>

                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={7}
                                                    className='h-24 text-center'
                                                    data-test='no-results'
                                                >
                                                    Loading...
                                                </TableCell>
                                            </TableRow>
                                        ) : paginatedData.length > 0 ? (
                                            paginatedData.map((progress, index) => {
                                                const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-muted/50';

                                                return (
                                                    <React.Fragment key={progress.accountId}>
                                                        <TableRow
                                                            className={cn(
                                                                rowClass,
                                                                'border-border border-solid border-t-0 border-l-0 border-r-0'
                                                            )}
                                                            data-test={`account-row-${progress.accountId}`}
                                                        >
                                                            <TableCell
                                                                className='h-14'
                                                                data-test='account-id'
                                                            >
                                                                {progress.accountId}
                                                            </TableCell>
                                                            <TableCell
                                                                className='h-14'
                                                                data-test='opt-in-timestamp'
                                                            >
                                                                {DateTime.from(progress.createdAt)?.format(
                                                                    'YYYY-MM-DD HH:mm:ss'
                                                                )}
                                                            </TableCell>

                                                            <TableCell className='h-14'>
                                                                <Button
                                                                    variant='ghost'
                                                                    size='sm'
                                                                    icon='external-link'
                                                                    className='!text-foreground'
                                                                    onClick={() =>
                                                                        redirectToAccount(progress.accountId)
                                                                    }
                                                                ></Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={7}
                                                    className='h-24 text-center'
                                                    data-test='no-results'
                                                >
                                                    No results.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            <PaginationTable
                                config={paginationConfig}
                                isAutoPosition={false}
                                selectClassName='top-auto bottom-full'
                            />
                        </DialogFooter>
                    </Dialog>
                )}
            </>
        );
    }
);
